import { z } from 'zod';
import { create } from 'zustand';

import type { FlowFlags, FlowFlagsEntry } from './types';

export const FLOW_FLAGS_STORAGE_KEY = 'snappy.flowFlags.v1';

const entrySchema = z.object({
    v1: z.object({ code: z.string(), at: z.number() }).optional(),
    v2: z.object({ at: z.number() }).optional(),
});

/** A malformed entry is dropped, never the whole map. */
function loadFlags(): FlowFlags {
    try {
        const raw = localStorage.getItem(FLOW_FLAGS_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        const flags: FlowFlags = {};
        for (const [accountHash, value] of Object.entries(parsed)) {
            const entry = entrySchema.safeParse(value);
            if (entry.success) flags[accountHash.toLowerCase()] = entry.data;
        }
        return flags;
    } catch {
        return {};
    }
}

function saveFlags(flags: FlowFlags): void {
    try {
        localStorage.setItem(FLOW_FLAGS_STORAGE_KEY, JSON.stringify(flags));
    } catch {
        // storage unavailable: the flag lasts for this page load only.
    }
}

interface FlowFlagsState {
    flags: FlowFlags;
    /** A non-V2 voucher validated in the feature-flags view. */
    setV1Code: (accountHash: string, code: string) => void;
    /** A V2 code redeemed from this browser. */
    markV2: (accountHash: string) => void;
    /** Re-read storage (tests, and another tab's write). */
    reload: () => void;
}

export const useFlowFlagsStore = create<FlowFlagsState>((set, get) => {
    const update = (accountHash: string, patch: FlowFlagsEntry) => {
        const key = accountHash.toLowerCase();
        const flags = {
            ...get().flags,
            [key]: { ...get().flags[key], ...patch },
        };
        saveFlags(flags);
        set({ flags });
    };
    return {
        flags: loadFlags(),
        setV1Code: (accountHash, code) =>
            update(accountHash, { v1: { code, at: Date.now() } }),
        markV2: (accountHash) =>
            update(accountHash, { v2: { at: Date.now() } }),
        reload: () => set({ flags: loadFlags() }),
    };
});

export function readFlowFlags(
    accountHash: string | null | undefined,
): FlowFlagsEntry | undefined {
    if (!accountHash) return undefined;
    return useFlowFlagsStore.getState().flags[accountHash.toLowerCase()];
}
